import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './FeaturesPage.module.css';

// ── Category palettes ─────────────────────────────────────────────────────
// All accents pulled from a warm palette that sits on the stone base.
// Explore: teal (brand primary)  |  Deep Dive: indigo (deep/analytical)
// Connect: teal-700 (darker)     |  AI Agent: amber (warm energy)
const CAT = {
  Explore:    { bg: '#f0fdfa', border: '#99f6e4', shadow: '#5eead466', accent: '#0d9488', tab: '#0d9488' },
  'Deep Dive': { bg: '#eef2ff', border: '#c7d2fe', shadow: '#a5b4fc66', accent: '#4f46e5', tab: '#4f46e5' },
  Connect:    { bg: '#f0fdfa', border: '#5eead4', shadow: '#2dd4bf66', accent: '#0f766e', tab: '#0f766e' },
  'AI Agent': { bg: '#fffbeb', border: '#fde68a', shadow: '#fcd34d66', accent: '#b45309', tab: '#d97706' },
};

const TAB_COLOR = { All: '#1c1917', ...Object.fromEntries(Object.entries(CAT).map(([k, v]) => [k, v.tab])) };

const features = [
  { title: 'Overview',              path: '/',                    icon: '🏠', category: 'Explore',    tagline: 'Your intelligence hub in one view.',                    bullets: ['Live market pulse and highlights', 'Quick access to every suite'] },
  { title: 'Smart Search',          path: '/smart-search-suite',  icon: '🔍', category: 'Explore',    tagline: 'Search news, filings and reports instantly.',            bullets: ['Real-time search across financial content', 'Instant answers on stocks and themes'] },
  { title: 'Live Themes',           path: '/live-themes',         icon: '📰', category: 'Explore',    tagline: 'AI-curated market updates.',       bullets: ['Real-time sentiment detection', 'All asset classes in one feed'] },
  { title: 'Unstructured Insights', path: '/unstructured-insights', icon: '📋', category: 'Explore',  tagline: 'Extract insights from text and video.',                  bullets: ['Filings and transcript analysis', 'Video timestamps and sentiment'] },
  { title: 'Entity Intelligence',   path: '/entity-intelligence', icon: '👀', category: 'Deep Dive',   tagline: 'All markets and data in one place.',                     bullets: ['Interactive dashboards by asset', 'Customizable views'] },
  { title: 'Research Analyst',      path: '/research-analyst',    icon: '🔬', category: 'Deep Dive',   tagline: 'Autonomous research across global markets.',             bullets: ['Cross-asset analysis', 'Auto-generated charts'] },
  { title: 'Historical Analysis',   path: '/historical-analysis', icon: '📊', category: 'Deep Dive',   tagline: 'Ask questions, get insights instantly.',                 bullets: ['Conversational data insights', 'Interactive charts on demand'] },
  { title: 'Predictive Signals',    path: '/signals-suite',       icon: '📈', category: 'Deep Dive',   tagline: 'Adaptive models for market moves.',                      bullets: ['Real-time predictions', 'Explainable signal logic'] },
  { title: 'Dynamic Visualization', path: '/visualization-suite', icon: '🗺️', category: 'Connect',   tagline: 'Turn data into interactive visuals.',                    bullets: ['AI-powered charts', 'Live-updating graphs'] },
  { title: 'Report & Idea Studio',         path: '/ideas-maker',         icon: '🧾', category: 'Connect',   tagline: 'Generate reports and presentations instantly.',          bullets: ['Custom reports and slide decks', 'Ready-to-share output'] },
  { title: 'Ask Fina',              path: '/fina-agent',          icon: '🤖', category: 'AI Agent',  tagline: 'Ask anything about markets.',                            bullets: ['Natural language market Q&A', 'Fast, grounded responses'] },
  { title: 'Fina Collective',            path: '/fina-collective',          icon: '🕸️', category: 'AI Agent',  tagline: 'Multi-agent intelligence working in parallel.',          bullets: ['Coordinated swarm of specialized agents', 'Deep, multi-step market research'] },
];

const CATEGORIES = ['All', 'Explore', 'Deep Dive', 'Connect', 'AI Agent'];

// ── Card ──────────────────────────────────────────────────────────────────
const FeatureCard = ({ title, path, icon, category, tagline, bullets, globalIndex }) => {
  const c = CAT[category] || {};
  return (
    <Link
      to={path}
      className={styles.card}
      style={{
        '--bg':     c.bg,
        '--border': c.border,
        '--shadow': c.shadow,
        '--accent': c.accent,
        '--delay':  `${globalIndex * 0.04}s`,
      }}
    >
      <div className={styles.cardTop}>
        <span className={styles.cardIcon}>{icon}</span>
        <span className={styles.badge}>{category}</span>
      </div>
      <h3 className={styles.cardTitle}>{title}</h3>
      <p className={styles.tagline}>{tagline}</p>
      <ul className={styles.bullets}>
        {bullets.map((b, i) => (
          <li key={i} className={styles.bullet}>
            <span className={styles.dot} />
            {b}
          </li>
        ))}
      </ul>
    </Link>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────
const FeaturesPage = () => {
  const [active, setActive] = useState('All');

  const filtered = active === 'All'
    ? features
    : features.filter(f => f.category === active);

  return (
    <div className={styles.page}>

      {/* ── Hero ──────────────────────────────────────────── */}
      <header className={styles.hero}>
        <p className={styles.eyebrow}>Platform Overview</p>
        <h1 className={styles.heroTitle}>
          <span className={styles.heroLight}>Deep analytics for</span>
          <span className={styles.heroBold}>smarter decisions</span>
        </h1>
        <p className={styles.heroSub}>
          A unified suite of AI tools built for financial intelligence —
          from live market data to autonomous research agents.
        </p>

        <div className={styles.heroStats}>
          <span className={styles.statPill}><strong>200+</strong> markets</span>
          <span className={styles.statPill}><strong>Live</strong> AI analysis</span>
        </div>
      </header>

      {/* ── Filter tabs ───────────────────────────────────── */}
      <nav className={styles.filterBar}>
        {CATEGORIES.map(cat => (
          <button
            key={cat}
            className={`${styles.filterTab} ${active === cat ? styles.filterTabActive : ''}`}
            style={{ '--c': TAB_COLOR[cat] }}
            onClick={() => setActive(cat)}
          >
            {CAT[cat] && <span className={styles.tabDot} />}
            {cat}
          </button>
        ))}
      </nav>

      {/* ── Flat grid ─────────────────────────────────────── */}
      <section className={styles.grid} key={active}>
        {filtered.map((f, i) => (
          <FeatureCard key={f.title} {...f} globalIndex={i} />
        ))}
      </section>


    </div>
  );
};

export default FeaturesPage;
